/**
 * DryRunPreview Component - SPEC-019 Task 7.1
 *
 * Modal component for previewing frontmatter changes before applying AI suggestions.
 * Shows before/after diff view with confirm/cancel actions.
 *
 * @module features/ai-button/components/DryRunPreview
 * @see SPEC-019 Requirements 4.2, 4.3, 4.4
 */

import { memo, useId, useRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/cn';
import { useTypedTranslation } from '../../../i18n';
import { useFocusTrap } from '../hooks/useFocusTrap';
import type { DryRunResult } from '../apply/dry-run';

// ============================================================================
// CVA Variants
// ============================================================================

/**
 * DryRunPreview modal variants using CVA
 */
export const dryRunPreviewVariants = cva(
  // Base styles
  ['fixed inset-0 z-50', 'flex items-center justify-center', 'bg-black/50'],
  {
    variants: {
      size: {
        default: [],
        large: [],
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

/**
 * Content panel variants
 */
export const contentPanelVariants = cva(
  ['bg-white rounded-lg shadow-xl', 'flex flex-col', 'max-h-[80vh] overflow-hidden'],
  {
    variants: {
      size: {
        default: ['w-full max-w-2xl'],
        large: ['w-full max-w-4xl'],
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export type DryRunPreviewVariants = VariantProps<typeof dryRunPreviewVariants>;

// ============================================================================
// Types
// ============================================================================

export interface DryRunPreviewProps extends DryRunPreviewVariants {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Dry-run result to display */
  dryRunResult: DryRunResult;
  /** Callback when user confirms apply */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
  /** Ref to trigger element for focus restoration */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** Additional className */
  className?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Code block for displaying YAML content
 */
function CodeBlock({
  content,
  testId,
  emptyMessage,
}: {
  content: string;
  testId: string;
  emptyMessage: string;
}) {
  if (!content) {
    return (
      <div
        data-testid={testId}
        className="p-3 bg-gray-50 rounded border border-gray-200 text-gray-400 text-sm italic"
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <pre
      data-testid={testId}
      className="p-3 bg-gray-50 rounded border border-gray-200 text-sm font-mono overflow-x-auto whitespace-pre-wrap"
    >
      {content}
    </pre>
  );
}

/**
 * Warning list component
 */
function WarningList({ warnings }: { warnings: string[] }) {
  const { t } = useTypedTranslation();

  if (warnings.length === 0) return null;

  return (
    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
      <h4 className="text-sm font-medium text-yellow-800 mb-2">{t('ai.dryRun.warnings')}</h4>
      <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
        {warnings.map((warning, index) => (
          <li key={index}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

/**
 * DryRunPreview Component
 *
 * Modal for previewing frontmatter changes before applying AI suggestions.
 *
 * Features:
 * - Before/After diff view
 * - Body unchanged indicator
 * - Warnings display
 * - Confirm/Cancel actions
 * - Focus trap for accessibility
 *
 * @see SPEC-019 Requirements 4.2, 4.3, 4.4
 */
export const DryRunPreview = memo(function DryRunPreview({
  isOpen,
  dryRunResult,
  onConfirm,
  onCancel,
  triggerRef,
  size,
  className,
}: DryRunPreviewProps) {
  const { t } = useTypedTranslation();
  const titleId = useId();
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap for accessibility
  useFocusTrap({
    enabled: isOpen,
    containerRef: modalRef,
    returnFocusRef: triggerRef,
  });

  // Don't render if not open
  if (!isOpen) return null;

  const { before, after, bodyUnchanged, warnings } = dryRunResult;
  const _hasChanges = before !== after || before !== '' || after !== '';
  const noFrontmatterChanges = before === after && before === '';

  return (
    <div
      className={cn(dryRunPreviewVariants({ size }), className)}
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget) {
          onCancel();
        }
      }}
    >
      <div
        ref={modalRef}
        className={cn(contentPanelVariants({ size }))}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            {t('ai.dryRun.title')}
          </h2>
          <button
            type="button"
            className="p-1 text-gray-400 hover:text-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={onCancel}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {noFrontmatterChanges ? (
            <div className="text-center py-8 text-gray-500">{t('ai.dryRun.noChanges')}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Before */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('ai.dryRun.before')}</h3>
                <CodeBlock content={before} testId="dry-run-before" emptyMessage="(empty)" />
              </div>

              {/* After */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('ai.dryRun.after')}</h3>
                <CodeBlock content={after} testId="dry-run-after" emptyMessage="(empty)" />
              </div>
            </div>
          )}

          {/* Body unchanged indicator */}
          <div
            className={cn(
              'mt-4 p-3 rounded text-sm',
              bodyUnchanged
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-orange-50 text-orange-700 border border-orange-200'
            )}
          >
            {bodyUnchanged ? t('ai.dryRun.bodyUnchanged') : t('ai.dryRun.bodyChanged')}
          </div>

          {/* Warnings */}
          <WarningList warnings={warnings} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            type="button"
            data-testid="dry-run-cancel"
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md',
              'text-gray-700 bg-white border border-gray-300',
              'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            )}
            onClick={onCancel}
          >
            {t('ai.dryRun.cancel')}
          </button>
          <button
            type="button"
            data-testid="dry-run-confirm"
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md',
              'text-white bg-blue-600',
              'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            )}
            onClick={onConfirm}
          >
            {t('ai.dryRun.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
});
